// A Tiptap Image node with width + alignment attributes and a NodeView that
// provides drag-to-resize handles and alignment controls. The stored attrs
// (`width` as a % string, `align`) are exactly what the reader honors.

import Image from "@tiptap/extension-image";
import { mergeAttributes } from "@tiptap/core";

export const ResizableImage = Image.extend({
  draggable: true,

  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: "70%",
        parseHTML: (el: HTMLElement) => el.style.width || el.getAttribute("width") || "70%",
        renderHTML: (attrs: any) => (attrs.width ? { style: `width:${attrs.width}` } : {}),
      },
      align: {
        default: "center",
        parseHTML: (el: HTMLElement) => el.getAttribute("data-align") || "center",
        renderHTML: (attrs: any) => ({ "data-align": attrs.align }),
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    return ["img", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const figure = document.createElement("figure");
      const img = document.createElement("img");
      const handle = document.createElement("span");
      const bar = document.createElement("div");

      const applyAlign = (a: string) => {
        figure.className = `ed-img ed-img-${a}`;
      };
      const setSrc = () => {
        img.src = node.attrs.src ?? "";
        img.alt = node.attrs.alt ?? "";
        img.style.width = node.attrs.width ?? "70%";
      };

      figure.contentEditable = "false";
      handle.className = "ed-img-handle";
      bar.className = "ed-img-bar";
      bar.innerHTML = `
        <button data-a="left" title="Align left">⬅</button>
        <button data-a="center" title="Center">▣</button>
        <button data-a="full" title="Full width">⬌</button>`;
      figure.append(img, handle, bar);
      applyAlign(node.attrs.align ?? "center");
      setSrc();

      // alignment
      bar.querySelectorAll("button").forEach((b) =>
        b.addEventListener("mousedown", (e) => {
          e.preventDefault();
          const a = (b as HTMLElement).dataset.a!;
          if (typeof getPos === "function")
            editor.chain().focus().command(({ tr }) => {
              tr.setNodeMarkup(getPos(), undefined, { ...node.attrs, align: a, width: a === "full" ? "100%" : node.attrs.width });
              return true;
            }).run();
        }),
      );

      // drag-to-resize
      handle.addEventListener("mousedown", (e) => {
        e.preventDefault();
        const startX = e.clientX;
        const startW = img.getBoundingClientRect().width;
        const parentW = (figure.parentElement?.getBoundingClientRect().width ?? startW) || startW;
        const onMove = (ev: MouseEvent) => {
          const next = Math.max(20, Math.min(100, ((startW + (ev.clientX - startX)) / parentW) * 100));
          img.style.width = `${next.toFixed(0)}%`;
        };
        const onUp = () => {
          document.removeEventListener("mousemove", onMove);
          document.removeEventListener("mouseup", onUp);
          if (typeof getPos === "function")
            editor.chain().focus().command(({ tr }) => {
              tr.setNodeMarkup(getPos(), undefined, { ...node.attrs, width: img.style.width });
              return true;
            }).run();
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
      });

      return {
        dom: figure,
        update: (updated) => {
          if (updated.type.name !== "image") return false;
          img.src = updated.attrs.src ?? "";
          img.alt = updated.attrs.alt ?? "";
          img.style.width = updated.attrs.width ?? "70%";
          applyAlign(updated.attrs.align ?? "center");
          return true;
        },
        ignoreMutation: () => true,
      };
    };
  },
});
